import { useCallback, useEffect, useState } from 'react'

import moment from 'moment-timezone'
import { DatePickerPreset, Input, Select } from 'twyne-new.ui'

import app from '@Stores/app'

import {
  BOOLEAN_VALUE_OPTIONS,
  DISPLAY_DATE_MASK,
  getFieldType,
  isListOperator,
  isRangeOperator,
} from '../utils/customFilters'

const VALUE_DEBOUNCE = 400

const NUMERIC_TYPES = ['long', 'integer']

function splitValues(value) {
  return String(value ?? '').split(',').map(item => item.trim())
}

function toMoment(value) {
  const text = String(value ?? '').trim()
  if (!text) return undefined

  const parsed = moment.tz(text, app.timezone)

  return parsed.isValid() ? parsed : undefined
}

function fromMoment(value) {
  return value ? moment(value).tz(app.timezone, true).format() : ''
}

export function getValuePlaceholder(operator) {
  if (isRangeOperator(operator)) return 'from, to'
  if (isListOperator(operator)) return 'value1, value2'

  return 'Value'
}

function TextValueInput({ value, numeric, placeholder, onChange }) {
  const [draft, setDraft] = useState(value ?? '')
  const isCommitted = String(value ?? '') === draft

  useEffect(() => {
    setDraft(value ?? '')
  }, [value])

  const commit = useCallback(() => {
    if (isCommitted) return
    onChange(draft)
  }, [draft, isCommitted, onChange])

  useEffect(() => {
    if (isCommitted) return

    const timeout = setTimeout(commit, VALUE_DEBOUNCE)

    return () => clearTimeout(timeout)
  }, [commit, isCommitted])

  return <Input
    type={numeric ? 'number' : 'text'}
    value={draft}
    onChange={event => setDraft(event.target.value)}
    onBlur={commit}
    onKeyDown={event => event.key === 'Enter' && commit()}
    placeholder={placeholder}
  />
}

function DateRangeValueInput({ value, onChange }) {
  const [start, end] = splitValues(value)
  const $gte = toMoment(start)
  const $lte = toMoment(end)

  return <DatePickerPreset
    mode="range"
    valueType="moment"
    mask={DISPLAY_DATE_MASK}
    numberOfMonths={2}
    value={$gte || $lte ? { $gte, $lte } : undefined}
    onChange={dates => onChange(`${fromMoment(dates?.$gte)}, ${fromMoment(dates?.$lte)}`)}
  />
}

function DateValueInput({ value, onChange }) {
  const [date] = splitValues(value)

  return <DatePickerPreset
    mode="single"
    valueType="moment"
    mask={DISPLAY_DATE_MASK}
    numberOfMonths={1}
    preset={false}
    value={toMoment(date)}
    onChange={selected => onChange(fromMoment(selected))}
  />
}

export default function CustomFilterValueInput({ collection, filter, onChange }) {
  const { operator, value } = filter
  const type = getFieldType(collection, filter.field)
  const isList = isListOperator(operator)
  const isRange = isRangeOperator(operator)

  if (type === 'boolean') {
    return <Select
      options={BOOLEAN_VALUE_OPTIONS}
      value={value || null}
      onChange={option => onChange(option?.value || '')}
      placeholder="Select the value"
    />
  }

  if (type === 'date' && isRange) {
    return <DateRangeValueInput value={value} onChange={onChange} />
  }

  if (type === 'date' && !isList) {
    return <DateValueInput value={value} onChange={onChange} />
  }

  return <TextValueInput
    value={value}
    numeric={NUMERIC_TYPES.includes(type) && !isList && !isRange}
    placeholder={getValuePlaceholder(operator)}
    onChange={onChange}
  />
}
